import React, { useEffect, useRef } from "react"
import { IconX } from "@tabler/icons-react"
import grugIcon from "images/profile_icons/grug.png"
import { useModalAnalytics } from "@love-irish/analytics"
import FloatingMenu from "./floating_menu"
import cn from "design_system/libs/cn"
import Tooltip from "design_system/core/components/tooltip"
import { useFloatingMenu } from "./context"

// Identifies this surface in the generic modal.opened analytics event.
const MODAL_NAME = "grug_menu"

const GREETINGS = [
  "Dia duit! How can I help?",
  "Conas atá tú? Need anything?",
  "Hey there! What's up?",
]

function getGreeting() {
  // Use a stable greeting based on the current hour to avoid constant changes
  const hour = new Date().getHours()
  // return GREETINGS[hour % GREETINGS.length] || GREETINGS[0]
  return GREETINGS[1]
}

export function FloatingMenuFab() {
  const {
    isGrugMenuOpen: open,
    openGrugMenu,
    closeGrugMenu,
    toggleGrugMenu,
  } = useFloatingMenu()
  const menuRef = useRef<HTMLDivElement>(null)
  const greeting = getGreeting()

  // Fires modal.opened (name: "grug_menu") whenever the assistant menu opens.
  useModalAnalytics(MODAL_NAME, open)

  // Keyboard shortcuts: Cmd+J (Mac) or Ctrl+J (Windows/Linux) to toggle, Escape to close
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "j") {
        event.preventDefault()
        toggleGrugMenu()
      } else if (event.key === "Escape" && open) {
        event.preventDefault()
        closeGrugMenu()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [toggleGrugMenu, closeGrugMenu, open])

  // Focus first menu item when panel opens
  useEffect(() => {
    if (open && menuRef.current) {
      const firstFocusable = menuRef.current.querySelector<HTMLElement>(
        'a, button, [tabindex]:not([tabindex="-1"])'
      )
      firstFocusable?.focus()
    }
  }, [open])

  return (
    <div className="fixed bottom-4 right-4 z-40">
      {/* FAB Button - hidden when panel is open */}
      {!open && (
        <Tooltip
          title={
            <>
              Grug: app assistant{" "}
              <kbd className="bg-dark-700 ml-2 rounded px-2 py-1">&#8984;J</kbd>
            </>
          }
          placement="top-end"
        >
          <button
            type="button"
            onClick={openGrugMenu}
            className="w-14 h-14 rounded-full shadow-lg overflow-hidden hover:shadow-xl transition-all bg-white border-2 border-gray-200 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 animate-in fade-in zoom-in-full duration-200"
            aria-label="Open assistant menu"
          >
            <img
              src={grugIcon}
              alt="Grug"
              className="w-full h-full pixel-art object-cover"
            />
          </button>
        </Tooltip>
      )}

      {/* Chat Panel - shown when open */}
      {open && (
        <div
          className={cn(
            "w-96 max-w-3xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden",
            "animate-in fade-in zoom-in-95 duration-200"
          )}
        >
          {/* Header */}
          <div className="p-4 ">
            <div className="flex items-start justify-between">
              <div className="text-xs">Your friendly site assistant</div>
              <button
                type="button"
                onClick={closeGrugMenu}
                className="p-1 rounded-full hover:bg-white/20 transition-colors -ml-1 -mt-1"
                aria-label="Close menu"
              >
                <IconX size={18} />
              </button>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="">
                <img
                  src={grugIcon}
                  alt="Grug"
                  width={64}
                  className="pixel-art rounded-full"
                />
              </div>
              <div>
                <div className="font-semibold">Grug</div>
                <div className="text-sm text-muted-foreground">{greeting}</div>
              </div>
            </div>

            <div className="pt-6">
              <p>Here are some links to help you get started</p>
            </div>
          </div>
          <div ref={menuRef} className="p-2">
            <FloatingMenu onClose={closeGrugMenu} />
          </div>
        </div>
      )}
    </div>
  )
}

export default FloatingMenuFab
